import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";

const ContactUs = () => {
  return (
    <div className="bg-[#0f172a] min-h-screen pt-24 pb-16 text-slate-100 overflow-hidden relative">
      <Helmet>
        <title>Contact Us - SyncMovie</title>
        <meta name="description" content="Get in touch with the SyncMovie team for support or inquiries." />
      </Helmet>

      {/* Decorative Blur */}
      <div className="absolute top-1/4 left-0 w-[400px] h-[400px] bg-blue-600/10 blur-[100px] rounded-full -z-10"></div>
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-indigo-600/10 blur-[100px] rounded-full -z-10"></div>

      <div className="container mx-auto px-6">
        <motion.div 
          className="max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-extrabold mb-4 tracking-tight">
              Get in <span className="text-blue-500">Touch</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-xl mx-auto leading-relaxed">
              Have questions or feedback? Our team is here to help you make your next movie night perfect.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Contact Form */}
            <motion.div 
              className="bg-slate-800/40 backdrop-blur-xl p-8 md:p-10 rounded-[2.5rem] border border-slate-700/50 shadow-2xl"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300 ml-1">Name</label>
                    <input
                      type="text"
                      placeholder="John Doe"
                      className="w-full px-5 py-4 bg-slate-900/50 border border-slate-700 rounded-2xl outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all placeholder:text-slate-600"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300 ml-1">Email</label>
                    <input
                      type="email"
                      placeholder="john@example.com"
                      className="w-full px-5 py-4 bg-slate-900/50 border border-slate-700 rounded-2xl outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all placeholder:text-slate-600"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-300 ml-1">Message</label>
                  <textarea
                    rows="5"
                    placeholder="How can we help?"
                    className="w-full px-5 py-4 bg-slate-900/50 border border-slate-700 rounded-2xl outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all placeholder:text-slate-600 resize-none"
                  ></textarea>
                </div>

                <motion.button
                  type="submit"
                  className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-2xl shadow-lg shadow-blue-500/20 transition-all active:scale-[0.98]"
                  whileHover={{ scale: 1.01 }}
                >
                  Send Message
                </motion.button>
              </form>
            </motion.div>

            {/* Info & Image */}
            <div className="space-y-12">
              <motion.div 
                className="grid grid-cols-1 sm:grid-cols-2 gap-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <div className="p-6 bg-slate-800/30 rounded-3xl border border-slate-700/30">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-500 mb-4">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                  </div>
                  <h4 className="font-bold mb-1">Email Us</h4>
                  <p className="text-slate-400 text-sm">support@syncmovie.com</p>
                </div>
                <div className="p-6 bg-slate-800/30 rounded-3xl border border-slate-700/30">
                  <div className="w-10 h-10 bg-purple-500/10 rounded-xl flex items-center justify-center text-purple-500 mb-4">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"/></svg>
                  </div>
                  <h4 className="font-bold mb-1">Live Chat</h4>
                  <p className="text-slate-400 text-sm">Available 24/7 in-app</p>
                </div>
              </motion.div>

              <motion.div 
                className="relative rounded-[2.5rem] overflow-hidden group shadow-2xl"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
              >
                <div className="absolute inset-0 bg-blue-600/20 mix-blend-overlay group-hover:bg-blue-600/0 transition-all duration-500"></div>
                <img
                  src="https://images.unsplash.com/photo-1596524430615-b46475ddff6e?auto=format&fit=crop&w=800&q=80"
                  alt="Support"
                  className="w-full aspect-video sm:aspect-square md:aspect-video object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactUs;
